import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, Loading, Empty, PageHeader, ExportBar, exportExcel, CHART_COLORS, ScoreBadge } from "./_shared";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const COMPETENCIES = ["Managing Others", "Communication", "Motivating Others", "Delivering Results", "Coaching And Developing", "Action Oriented"];
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 } };
const DIMS = [
  { value: "batch", label: "Batch vs Batch" },
  { value: "department", label: "Department vs Department" },
  { value: "role", label: "Role / Experience Level" },
  { value: "program", label: "Program vs Program" },
];

export default function BenchmarkReports() {
  const [dimension, setDimension] = useState("batch");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/benchmark?dimension=${dimension}`).then((r) => setData(r.data.groups)).finally(() => setLoading(false));
  }, [dimension]);

  // Build radar dataset: one entry per competency with a column per group
  const radarData = COMPETENCIES.map((c) => {
    const row = { competency: c };
    (data || []).forEach((g) => { row[g.group] = g.competencies[c] || 0; });
    return row;
  });

  const handleExcel = () =>
    exportExcel([{
      name: "Benchmark",
      rows: (data || []).map((g) => ({
        Group: g.group, Participants: g.participants, "Avg Score": g.avg_score,
        "Completion %": g.completion_pct, "Readiness %": g.readiness, ...g.competencies,
      })),
    }], `benchmark-${dimension}.xlsx`);

  return (
    <div data-testid="benchmark-page">
      <PageHeader title="Benchmark Reports"
        description="Compare performance across batches, departments, roles and programs side by side."
        right={data?.length > 0 && <ExportBar onExcel={handleExcel} />} />

      <div className="mb-6 max-w-xs">
        <label className="text-[11px] text-stone-500 mb-1 block">Compare by</label>
        <Select value={dimension} onValueChange={setDimension}>
          <SelectTrigger data-testid="benchmark-dimension-select" className="h-10 border-stone-300 rounded-lg bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIMS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : !data || data.length === 0 ? <Empty /> : (
        <div className="space-y-6">
          <Panel title="Average Score Comparison" testid="benchmark-bar">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0eeec" />
                <XAxis dataKey="group" tick={{ fontSize: 11 }} stroke="#a8a29e" interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#a8a29e" />
                <Tooltip {...tip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="avg_score" name="Avg score" fill="#b81f24" radius={[6, 6, 0, 0]} />
                <Bar dataKey="readiness" name="Readiness %" fill="#0f766e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Competency Radar Comparison" testid="benchmark-radar">
            <ResponsiveContainer width="100%" height={380}>
              <RadarChart data={radarData} outerRadius="70%">
                <PolarGrid stroke="#e7e5e4" />
                <PolarAngleAxis dataKey="competency" tick={{ fontSize: 11, fill: "#57534e" }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#d6d3d1" />
                {(data || []).slice(0, 6).map((g, i) => (
                  <Radar key={g.group} dataKey={g.group} stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.12} strokeWidth={2} />
                ))}
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip {...tip} />
              </RadarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Comparison Table" testid="benchmark-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                    <th className="py-3 px-2 font-medium">Group</th>
                    <th className="py-3 px-2 font-medium text-center">N</th>
                    <th className="py-3 px-2 font-medium text-center">Avg Score</th>
                    <th className="py-3 px-2 font-medium text-center">Completion</th>
                    <th className="py-3 px-2 font-medium text-center">Readiness</th>
                    {COMPETENCIES.map((c) => <th key={c} className="py-3 px-2 font-medium text-center">{c.split(" ")[0]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.map((g) => (
                    <tr key={g.group} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-2 font-medium text-stone-800">{g.group}</td>
                      <td className="py-3 px-2 text-center text-stone-600">{g.participants}</td>
                      <td className="py-3 px-2 text-center"><ScoreBadge value={g.avg_score} /></td>
                      <td className="py-3 px-2 text-center text-stone-600 tabular-nums">{g.completion_pct}%</td>
                      <td className="py-3 px-2 text-center text-stone-600 tabular-nums">{g.readiness}%</td>
                      {COMPETENCIES.map((c) => <td key={c} className="py-3 px-2 text-center text-stone-600 tabular-nums">{g.competencies[c] || "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
